/**
 * ============================================================
 * Cinnamon Kitchen — EDD Widget (v6.0 — Capability model)
 * File: assets/edd-widget.js
 *
 * v6.0: Product categorization now reads `custom.delivery_types` metafield
 * via data attributes set by the Liquid snippet. Same capability model as
 * the cart delivery picker v8.x.
 *
 * Capability flags (from Liquid → data attributes):
 *   isSdd       — product has same-day capability in NCR
 *   isFreshBake — product has next-day capability in NCR
 *   isPanIndia  — product can be couriered nationwide
 *   isNcrOnly   — product CANNOT ship outside NCR (lacks pan_india)
 *
 * PDP behavior per pincode:
 *   NCR pincode:
 *     isSdd + before cutoff       → "Delivered today!"
 *     isSdd + after cutoff        → "Delivery by tomorrow"
 *     isFreshBake (no sdd)        → "Delivery by tomorrow"
 *     isPanIndia only (no sdd/fb) → "Delivery by tomorrow"
 *   Non-NCR pincode:
 *     isPanIndia (has pan_india)   → zone-based EDD (2-5 days)
 *     isNcrOnly (no pan_india)     → error "Available In Delhi NCR Only"
 *
 * Cutoff now uses both hour AND minute (ck_sdd_cutoff_hour +
 * ck_sdd_cutoff_minute), matching the cart widget precision.
 * ============================================================
 */

// ---------------------------------------------------------------------------
// Delhi NCR serviceable pincodes
// ---------------------------------------------------------------------------
const DELHI_NCR_PINCODES = new Set([
  '110001','110002','110003','110004','110005','110006','110007','110008','110009','110010',
  '110011','110012','110013','110014','110015','110016','110017','110018','110019','110020',
  '110021','110022','110023','110024','110025','110026','110027','110028','110029','110030',
  '110031','110032','110033','110034','110035','110036','110037','110038','110039','110040',
  '110041','110042','110043','110044','110045','110046','110047','110048','110049','110050',
  '110051','110052','110053','110054','110055','110056','110057','110058','110059','110060',
  '110061','110062','110063','110064','110065','110066','110067','110068','110069','110070',
  '110072','110073','110074','110075','110076','110077','110078','110079','110080','110081',
  '110082','110083','110084','110085','110086','110087','110088','110089','110090','110091',
  '110092','110093','110094','110095','110096','110097','110098','110099',
  '121001','121002','121003','121004','121005','121006','121007','121008','121009','121011',
  '121101','121103','121104','121105','121106','121107',
  '122001','122002','122003','122004','122005','122006','122008','122009','122010','122011',
  '122015','122016','122017','122018','122101','122104','122105','122106','122107','122108',
  '122505',
  '123413','123414','123502','123504','123505','123506',
  '201001','201002','201003','201004','201005','201006','201007','201008','201009','201010',
  '201011','201012','201013','201014','201017','201102','201301','201302','201303','201304',
  '201305','201306','201307','201308','201309','201310',
  '203207','203208',
  '210306','211011'
]);

// ---------------------------------------------------------------------------
// EDD zone detection (3-digit prefix)
// ---------------------------------------------------------------------------
const EDD_METRO_PREFIXES = new Set([
  '110','111','120','121','122','123','124','125','131','132','133','134','135','201',
  '400','401','402','403','404','405','406','407','408','409','410','421',
  '560','561','562','563','564',
  '600','601','602','603','604',
  '500','501','502','503',
  '700','701','702','703',
  '411','412',
  '380','382','383',
  '395',
]);

const EDD_TIER2_PREFIXES = new Set([
  '302','303','226','227','208','209','282','462','463','452','453',
  '440','441','422','423','390','391','360','361','641','642','682',
  '683','530','531','140','160','143','141','142','800','801','834',
  '492','751','752','781','570','571','342','324','695','625','520','521','506','575',
]);

// ---------------------------------------------------------------------------
// Day-label helpers
// ---------------------------------------------------------------------------
function makeDayLabel(minDays, maxDays, afterCutoff = false) {
  if (minDays === 0 && maxDays === 0) return 'Delivered today!';
  if (minDays === 1 && maxDays === 1) return afterCutoff ? 'Delivery by tomorrow' : 'Delivery within 24 hrs';
  if (minDays === maxDays) return `Delivery in ${minDays} day${minDays > 1 ? 's' : ''}`;
  return `Delivery in ${minDays}\u2013${maxDays} days`;
}

function isoDate(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

// ---------------------------------------------------------------------------
// EDDWidget class
// ---------------------------------------------------------------------------
class EDDWidget {
  constructor(container) {
    this.el  = container;
    this.bid = container.dataset.block;

    // ── Cutoff (now uses both hour AND minute, matching cart widget) ───────
    this.cutoffHour   = parseInt(container.dataset.cutoffHour, 10) || 15;
    this.cutoffMinute = parseInt(container.dataset.cutoffMinute, 10) || 0;

    this.blockedDates = (container.dataset.blocked || '').split(',').map(s => s.trim()).filter(Boolean);
    this.deliverSun   = container.dataset.sundays !== 'false';

    // ── Messages (fall back to sensible defaults if attribute absent) ──────
    this.msgNA          = container.dataset.msgNa       !== undefined ? container.dataset.msgNa       : 'Sorry, delivery is not available to this pincode.';
    this.msgNaNCR       = container.dataset.msgNaNcr    !== undefined ? container.dataset.msgNaNcr    : 'Available In Delhi NCR Only';
    this.msgNaFreshBake = container.dataset.msgNaFreshBake !== undefined ? container.dataset.msgNaFreshBake : '';
    this.msgEligible    = container.dataset.msgEligible !== undefined ? container.dataset.msgEligible : 'Yay! Your pincode is eligible for delivery.';
    this.widgetTitle    = container.dataset.widgetTitle  !== undefined ? container.dataset.widgetTitle  : 'Delivery Options';
    this.changeLabel    = container.dataset.changeLabel  !== undefined ? container.dataset.changeLabel  : 'Change';

    // ── v6.0: capability flags from metafield (via Liquid data attributes) ─
    this.isSdd       = container.dataset.isSdd       === 'true';
    this.isFreshBake = container.dataset.isFreshBake === 'true';
    this.isPanIndia  = container.dataset.isPanIndia  === 'true';
    this.isNcrOnly   = container.dataset.isNcrOnly   === 'true';

    // ── Config JSON (zones, overrides, same-day messages) ─────────────────
    const raw = document.getElementById(`edd-data-${this.bid}`);
    const cfg = raw ? JSON.parse(raw.textContent) : { zones: {}, overrides: [] };
    this.zones     = cfg.zones     || {};
    this.overrides = cfg.overrides || [];

    const rawSameDay = cfg.sameDay || {};
    this.sameDay = {
      cutoff:    rawSameDay.cutoff    !== undefined ? rawSameDay.cutoff    : 15,
      msgBefore: rawSameDay.msgBefore !== undefined ? rawSameDay.msgBefore : 'Delivered today!',
      msgAfter:  rawSameDay.msgAfter  !== undefined ? rawSameDay.msgAfter  : 'Delivery by tomorrow',
    };

    this.overrideMap = {};
    this.overrides.forEach(o => { this.overrideMap[o.pincode] = o; });

    // ── DOM refs ──────────────────────────────────────────────────────────
    this.inputEl       = document.getElementById(`edd-input-${this.bid}`);
    this.checkBtnEl    = document.getElementById(`edd-check-btn-${this.bid}`);
    this.changeBtnEl   = document.getElementById(`edd-change-btn-${this.bid}`);
    this.titleEl       = document.getElementById(`edd-title-${this.bid}`);
    this.resultEl      = document.getElementById(`edd-result-${this.bid}`);
    this.eligibilityEl = document.getElementById(`edd-eligibility-${this.bid}`);
    this.headlineEl    = document.getElementById(`edd-headline-${this.bid}`);
    this.errorEl       = document.getElementById(`edd-error-${this.bid}`);
    this.formRowEl     = document.getElementById(`edd-form-row-${this.bid}`);

    if (!this.inputEl || !this.checkBtnEl) return;
    this._bindEvents();
    this._restorePincode();
  }

  // ── Events ----------------------------------------------------------------
  _bindEvents() {
    this.checkBtnEl.addEventListener('click', () => this.check());
    this.inputEl.addEventListener('keydown', e => { if (e.key === 'Enter') this.check(); });
    this.inputEl.addEventListener('input', () => {
      this.inputEl.value = this.inputEl.value.replace(/\D/g, '').slice(0, 6);
    });
    if (this.changeBtnEl) {
      this.changeBtnEl.addEventListener('click', () => this._resetToInput());
    }
  }

  _restorePincode() {
    // Do not restore — input starts blank on every PDP visit
  }

  // ── Minute-precise cutoff check (matches cart widget v8.2) ────────────────
  _isBeforeCutoff() {
    const now = new Date();
    const h = now.getHours();
    const m = now.getMinutes();
    return (h < this.cutoffHour) || (h === this.cutoffHour && m < this.cutoffMinute);
  }

  // ── Main check ------------------------------------------------------------
  check(silent = false) {
    const pincode = this.inputEl.value.trim();

    if (!/^\d{6}$/.test(pincode)) {
      this._renderError('Please enter a valid 6-digit pincode.');
      return;
    }

    if (!silent) this._renderLoading();

    const isNCRPincode = DELHI_NCR_PINCODES.has(String(pincode).trim());
    const beforeCutoff = this._isBeforeCutoff();

    // ══════════════════════════════════════════════════════════════════════
    // BRANCH A: Delhi NCR pincode — NCR-fast capability wins
    // ══════════════════════════════════════════════════════════════════════
    if (isNCRPincode) {

      // 1. Fresh Bake (after tie-break) → always next-day in NCR
      if (this.isFreshBake) {
        this._renderResult(pincode, this.msgEligible, this.sameDay.msgAfter);
        return;
      }

      // 2. SDD product → same-day before cutoff, next-day after
      if (this.isSdd) {
        if (beforeCutoff) {
          this._renderResult(pincode, this.msgEligible, this.sameDay.msgBefore);
        } else {
          this._renderResult(pincode, this.msgEligible, this.sameDay.msgAfter);
        }
        return;
      }

      // 3. Pure pan_india product (no sdd/fresh_bake) → next-day in NCR
      //    This matches the cart S3 behavior: NCR customer, all pan_india →
      //    next-day calendar. On PDP we just show the text.
      if (this.isPanIndia) {
        this._renderResult(pincode, this.msgEligible, this.sameDay.msgAfter);
        return;
      }

      // 4. Fallback (should not happen if metafield is set; untagged products
      //    are treated as pan_india in Liquid) → next-day message
      this._renderResult(pincode, this.msgEligible, this.sameDay.msgAfter);
      return;
    }

    // ══════════════════════════════════════════════════════════════════════
    // BRANCH B: Non-NCR pincode — deliverable only if pan_india
    // ══════════════════════════════════════════════════════════════════════

    // NCR-only product (no pan_india) → cannot ship here
    if (this.isNcrOnly) {
      // Use the fresh-bake-specific message if applicable, else the NCR-only badge text
      const msg = (this.isFreshBake && this.msgNaFreshBake) ? this.msgNaFreshBake : this.msgNaNCR;
      this._renderError(msg);
      return;
    }

    // ── Pan India product — zone-based EDD ─────────────────────────────────
    const afterCutoff = !beforeCutoff;

    // 1. Explicit pincode override
    const override = this.overrideMap[pincode];
    if (override) {
      if (override.serviceable === false || override.serviceable === 'false') {
        this._renderError(this.msgNA);
        return;
      }
      const zone = this.zones[override.zone] || this.zones['tier3'];
      if (override.msg) {
        this._renderResult(pincode, null, override.msg);
      } else {
        const label = makeDayLabel(zone.min, zone.max, afterCutoff);
        this._renderResult(pincode, this.msgEligible, label);
      }
      return;
    }

    // 2. Zone detection by prefix
    const zone = this._detectZone(pincode);
    if (!zone) {
      this._renderError(this.msgNA);
      return;
    }

    // Zone has a custom message → show directly
    if (zone.msg && zone.msg.trim()) {
      this._renderResult(pincode, null, zone.msg);
      return;
    }

    // Build day-label from zone min/max, adjusted for cutoff
    let min = zone.min;
    let max = zone.max;
    if (afterCutoff) {
      min = Math.max(min + 1, 1);
      max = max + 1;
    }
    const label = makeDayLabel(min, max, afterCutoff);
    this._renderResult(pincode, this.msgEligible, label);
  }

  // ── Zone detection --------------------------------------------------------
  _detectZone(pincode) {
    if (parseInt(pincode[0], 10) === 0) return null;
    const p3 = pincode.slice(0, 3);
    if (EDD_METRO_PREFIXES.has(p3)) return this.zones['metro'] || this.zones['tier3'];
    if (EDD_TIER2_PREFIXES.has(p3)) return this.zones['tier2'] || this.zones['tier3'];
    return this.zones['tier3'] || null;
  }

  // ── Render: result with day-label ─────────────────────────────────────────
  _renderResult(pincode, eligibilityText, headlineText) {
    if (this.titleEl) {
      this.titleEl.innerHTML = `${this.widgetTitle} for <strong>${pincode}</strong><button type="button" class="edd-widget__change-inline" id="edd-change-inline-${this.bid}">${this.changeLabel}</button>`;
      const inlineBtn = document.getElementById(`edd-change-inline-${this.bid}`);
      if (inlineBtn) inlineBtn.addEventListener('click', () => this._resetToInput());
    }

    if (this.changeBtnEl) this.changeBtnEl.style.display = 'none';
    if (this.formRowEl)   this.formRowEl.style.display   = 'none';

    if (this.eligibilityEl) {
      if (eligibilityText) {
        this.eligibilityEl.textContent = eligibilityText;
        this.eligibilityEl.style.display = 'block';
      } else {
        this.eligibilityEl.style.display = 'none';
      }
    }

    if (this.headlineEl) {
      this.headlineEl.textContent = headlineText;
      this.headlineEl.classList.remove('edd-widget__headline--loading');
    }

    if (this.resultEl) this.resultEl.classList.add('is-visible');
    this._hideError();
  }

  // ── Render: loading -------------------------------------------------------
  _renderLoading() {
    if (this.headlineEl) {
      this.headlineEl.textContent = '';
      this.headlineEl.classList.add('edd-widget__headline--loading');
    }
    if (this.eligibilityEl) this.eligibilityEl.style.display = 'none';
    if (this.resultEl) this.resultEl.classList.add('is-visible');
    this._hideError();
  }

  // ── Render: error ---------------------------------------------------------
  _renderError(message) {
    if (this.titleEl) this.titleEl.innerHTML = this.widgetTitle;
    if (this.changeBtnEl) this.changeBtnEl.style.display = 'none';
    if (this.formRowEl)   this.formRowEl.style.display   = 'flex';

    if (this.resultEl) this.resultEl.classList.remove('is-visible');
    if (this.errorEl) {
      this.errorEl.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <circle cx="12" cy="16" r="0.5" fill="currentColor"/>
        </svg>
        <span>${message}</span>`;
      this.errorEl.classList.add('is-visible');
    }
  }

  // ── Reset to input mode (Change button) ───────────────────────────────────
  _resetToInput() {
    if (this.titleEl) this.titleEl.innerHTML = this.widgetTitle;
    if (this.formRowEl)   this.formRowEl.style.display   = 'flex';
    if (this.changeBtnEl) this.changeBtnEl.style.display = 'none';
    if (this.resultEl) this.resultEl.classList.remove('is-visible');
    this._hideError();
    if (this.inputEl) {
      this.inputEl.value = '';
      this.inputEl.focus();
    }
    try { localStorage.removeItem('ck_edd_pincode'); } catch (_) {}
  }

  _hideError() {
    if (this.errorEl) this.errorEl.classList.remove('is-visible');
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------
function initEDDWidgets() {
  document.querySelectorAll('[id^="edd-widget-"]').forEach(el => {
    if (!el.dataset.eddInit) {
      el.dataset.eddInit = '1';
      new EDDWidget(el);
    }
  });
}

function reinitEDDWidgets() {
  document.querySelectorAll('[id^="edd-widget-"]').forEach(el => {
    delete el.dataset.eddInit;
    new EDDWidget(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initEDDWidgets);
} else {
  initEDDWidgets();
}

document.addEventListener('shopify:section:load', reinitEDDWidgets);
document.addEventListener('shopify:block:select', reinitEDDWidgets);
document.addEventListener('shopify:section:reorder', reinitEDDWidgets);